<template>
  <div>
    <gg-modal :is-active="isShow" @close="closeModal">
      <template v-slot:toolbar>
        Renewal Options for Your Hosting Plan
      </template>
      <template v-slot:body>
        <!-- Error State -->
        <v-card-text v-if="renewalLoadError">
          <v-row>
            <v-col cols="12" class="d-flex flex-column align-center">
              <v-icon color="error" size="64">mdi-close-circle</v-icon>
              <h4 class="mt-3 text-center">There was a problem loading your renewal options.</h4>
              <h4 v-if="renewalLoadErrorMessage" class="mt-2 text-center red--text font-weight-bold">
                {{ renewalLoadErrorMessage }}
              </h4>
              <h4 class="mt-2 text-center">Please contact us for assistance.</h4>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- Loading State -->
        <v-card-text v-else-if="loadingRenewalPricing">
          <v-row>
            <v-col cols="12" class="d-flex flex-column align-center justify-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                size="62"
              />
              <p class="mt-4 mb-0 grey--text">Loading renewal options...</p>
            </v-col>
          </v-row>
        </v-card-text>
        <!-- Form Content -->
        <v-card-text v-else-if="!renewCompleted">
          <v-row>
            <v-col cols="12">
              <p class="mb-0">
                Your current plan renews on
                {{ new Date(currentService.renewaldate).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) }}.
                Choose a term below to lock in savings and avoid price increases.
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <h5>Your Current Plan</h5>
              {{ currentService.name }} - {{ currentCost }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <h5>Choose Your Renewal Term:</h5>
              <v-radio-group
                v-model="selectedPeriod"
                class="mt-1 pt-0"
              >
                <v-row
                  v-for="(priceDetails, planName) in renewalPricing"
                  :key="planName"
                  class="my-2 py-0"
                >
                  <v-col cols="12" style="border: 1px solid grey; border-radius: 24px" class="my-0 py-0" :class="planName === selectedPeriod ? 'active' : ''">
                    <v-radio
                      class="py-0 my-3"
                      :value="planName"
                    >
                      <template v-slot:label>
                        <v-row>
                          <v-col cols="6" class="text-capitalize">
                            {{ planName }}
                            <v-chip
                              v-if="bestDealPlanName === planName"
                              class="mb-0 ml-1"
                              small
                              color="primary"
                            >Best Deal</v-chip>
                          </v-col>
                          <v-col cols="6" class="text-right">
                            <span
                              v-show="priceDetails.has_discount"
                              class="primary--text mr-1">
                              <strong>Save {{ priceDetails.discount_percent }}%</strong>
                              -
                            </span>
                            <span
                              v-show="priceDetails.has_discount"
                              class="grey--text text--darken-2 text-decoration-line-through mr-1"
                              style="font-size: 0.85rem;"
                            >
                              ${{ parseFloat(priceDetails.standard_cost).toFixed(2) }}
                            </span>
                            <span class="mr-1">${{ parseFloat(priceDetails.total).toFixed(2) }}</span>
                          </v-col>
                        </v-row>
                      </template>
                    </v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0 pt-5 d-flex flex-column">
              <h5>Payment Method</h5>
              <strong v-if="errorMessage" class="red--text pb-1">{{ errorMessage }}</strong>
            </v-col>
            <v-col cols="12" class="py-1">
              <payment-gateway
                :compact="true"
                ref="paymentGateway"
                @gateway="listenGateway"
                @card="listenCard"
                @card-switch="listenCardNew"
              />
                <form
                  v-if="paymentGateway === 'paypal'"
                  method="post"
                  ref="frm_paypal"
                  style="display:none;"
                >
                  <input type="hidden" name="cmd" value="_s-xclick">
                  <input type="text" ref="paypal_encrypted" name="encrypted">
                  <input type="submit" value="">
                </form>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else>
          <v-row>
            <v-col cols="12" class="d-flex flex-column align-center">
              <v-icon color="green" size="64">mdi-check-circle</v-icon>
              <h4 class="mt-3">It's done, you're all set! Thank you for your business!</h4>
              <h4>You saved a total of <span class="font-bold primary--text">${{ totalSavedFormatted }}</span> today by renewing early.</h4>
              <h4>Your next renewal date is on {{ nextDueDate }}.</h4>
              <gg-btn primary @click="closeModalAfterRenewal">Go Back</gg-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </template>
      <template v-slot:actions>
        <template v-if="loadingRenewalPricing">
          <!-- No actions during loading -->
        </template>
        <template v-else-if="renewalLoadError">
          <gg-btn @click="handleStartChat" style="text-transform: none !important;">Start a Chat</gg-btn>
          <gg-btn primary @click="handleOpenTicket" style="text-transform: none !important;">Open a Ticket</gg-btn>
          <gg-btn secondary @click="closeModal">Cancel</gg-btn>
        </template>
        <template v-else-if="!renewCompleted">
          <gg-btn secondary @click="closeModal">Cancel</gg-btn>
          <gg-btn :loading="loading" :disabled="!isPaymentFormReady" @click="submitRenew">Renew Now</gg-btn>
        </template>
      </template>
    </gg-modal>
    <auto-renew-modal ref="autoRenewModal"></auto-renew-modal>
    <show-invoice-modal
      ref="showInvoiceModal"
      :invoice-id="invoiceId"
      title="Proceed to invoice"
    ></show-invoice-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AutoRenewModal from './AutoRenewModal.vue'
import ShowInvoiceModal from './ShowInvoiceModal.vue'
import PaymentGateway from '@/components/Common/PaymentGateway'
import { cancellationTypes } from '@/consts/cancellations'
import { SERVICE_BILLING_PERIODS } from '@/consts/service'
import { PRODUCTS_BILLING_PERIODS } from '@/consts/main'
import { openChat } from '@/helpers/helpers'

const paymentGateways = {
  CREDITCARD: 'credit_card',
  PAYPAL: 'paypal',
}

const paymentDataResolver = {
  [paymentGateways.CREDITCARD]: (cardModel, isNewCard) => ({
    method: 'creditcard',
    cvv: cardModel.payment_cvv,
    cc: 'primary',
    cardnum: isNewCard ? cardModel.cardnum : '',
    expdate: isNewCard ? cardModel.expdate : '',
    cardnum_cvv: cardModel.cardnum_cvv
  }),
  [paymentGateways.PAYPAL]: () => ({
    method: paymentGateways.PAYPAL,
  }),
}

export default {
  name: 'RenewNowModal',

  components: {
    AutoRenewModal,
    ShowInvoiceModal,
    PaymentGateway,
  },

  props: {
    serviceId: {
      type: [String, Number],
      default: null,
    },
    preferredTerm: {
      type: String,
      default: null,
    },
  },

  data () {
    return {
      cancellationTypes,
      isShow: false,
      minPerMonth: 0,
      bestDealIndex: null,
      selectedPeriod: null,
      loading: false,
      loadingRenewalPricing: false,
      invoiceId: '',
      cardModel: {
        cardnum: '',
        cardnum_cvv: '',
        expdate: ''
      },
      isNewCard: false,
      paymentGateway: '',
      errorMessage: '',
      renewCompleted: false,
      nextDueDate: '',
      totalSaved: '',
      renewalLoadError: false,
      renewalLoadErrorMessage: '',
    }
  },

  computed: {
    ...mapGetters({
      SessionProfile: 'SessionProfile',
      services: 'services',
      products: 'products/products',
      renewalPricing: 'products/renewalPricing',
      hosting: 'hosting',
    }),
    totalSavedFormatted () {
      const raw = (this.totalSaved ?? '').toString().replace(/[^0-9.-]/g, '')
      const n = Number(raw)
      return Number.isFinite(n) ? n.toFixed(2) : '0.00'
    },
    currentService () {
      const id = this.serviceId || this.$route.params.hostingid
      return this.services.find((service) => service.id === id)
    },
    currentCost () {
      switch (this.currentService.renewalterm) {
        case SERVICE_BILLING_PERIODS.MONTHLY:
          return `$${this.currentService.renewalcost}/1 Month`
        case SERVICE_BILLING_PERIODS.QUARTERLY:
          return `$${this.currentService.renewalcost}/3 Month`
        case SERVICE_BILLING_PERIODS.SEMIANNUALLY:
          return `$${this.currentService.renewalcost}/6 Month`
        case SERVICE_BILLING_PERIODS.BIENNIALLY:
          return `$${this.currentService.renewalcost}/2 Years`
        case SERVICE_BILLING_PERIODS.TRIENNIALLY:
          return `$${this.currentService.renewalcost}/3 Years`
        case SERVICE_BILLING_PERIODS.ANNUALLY:
        default:
          return `$${this.currentService.renewalcost}/Year`
      }
    },
    currentProduct () {
      let foundProduct = this.products.find((product) => {
        return product.name.toLowerCase() === this.currentService.name.toLowerCase()
      })
      if (!foundProduct) {
        foundProduct = {
          name: this.currentService.name,
          locations: [
            {
              iso: this.currentService.flag,
              pricing: [
                {
                  term: this.currentService.renewalterm.toLowerCase(),
                  price: this.currentService.renewalcost,
                  permonth: this.serviceCalculatePricePerMonth(this.currentService.renewalcost, this.currentService.renewalterm),
                }
              ]
            }
          ]
        }
      }
      return foundProduct
    },
    currentProductLocation () {
      return this.currentProduct?.locations.find((location) => {
        return location.iso === this.currentService.flag
      })
    },
    currentProductLocationPricing () {
      return this.currentProductLocation?.pricing.toReversed()
    },
    bestDealPlanName () {
      const bestPlan = Object.entries(this.renewalPricing).reduce((bestPlan, [planName, priceDetails]) => {
        if (!bestPlan || priceDetails.discount_percent > bestPlan.discount_percent) {
          return { planName, discount_percent: priceDetails.discount_percent }
        }
        return bestPlan
      }, null)
      // Only return a best deal if there's actually a discount
      if (bestPlan && bestPlan.discount_percent > 0) {
        return bestPlan.planName
      }
      return null
    },
    isPaymentFormReady () {
      // PayPal doesn't require card fields
      if (this.paymentGateway === 'paypal') {
        return true
      }
      // Credit card requires CVV at minimum
      if (!this.cardModel.cardnum_cvv) {
        return false
      }
      // New card also requires card number and expiry date
      if (this.isNewCard) {
        return !!(this.cardModel.cardnum && this.cardModel.expdate)
      }
      return true
    },
  },

  methods: {
    ...mapActions({
      getProducts: 'products/getProducts',
      renewEarly: 'products/renewEarly',
      getRenewalPricing: 'products/getRenewalPricing',
      reportError: 'reportError',
      reportSuccess: 'reportSuccess',
    }),
    renewalCost (price) {
      if (!price) {
        return ''
      }
      let term = ''
      switch (price.term) {
        case PRODUCTS_BILLING_PERIODS.TRIENNIALLY:
          term = '3 Years'
          break
        case PRODUCTS_BILLING_PERIODS.BIENNIALLY:
          term = '2 Years'
          break
        case PRODUCTS_BILLING_PERIODS.ANNUALLY:
          term = '1 Year'
          break
        case PRODUCTS_BILLING_PERIODS.SEMIANNUALLY:
          term = '6 Months'
          break
        case PRODUCTS_BILLING_PERIODS.QUARTERLY:
          term = '3 Months'
          break
        case PRODUCTS_BILLING_PERIODS.MONTHLY:
          term = '1 Month'
          break
      }
      return `$${price.price} / ${term}`
    },
    async submitRenew () {
      const valid = await this.$refs.paymentGateway.validateForm()
      if (!valid) return

      const paymentData = paymentDataResolver[this.paymentGateway](this.cardModel, this.isNewCard)

      this.loading = true
      try {
        const response = await this.renewEarly({
          service_id: this.currentService.id,
          term: this.selectedPeriod,
          total: this.renewalPricing[this.selectedPeriod].total,
          ...paymentData,
        })

        this.nextDueDate = response.next_due_date
        this.totalSaved = response.total_saved

        this.reportSuccess('Your request has been received.')
        this.renewCompleted = true
      } catch (e) {
        this.reportError(e)
        this.errorMessage = typeof e === 'string' ? e : e.message || 'An error occurred during renewal.'
      } finally {
        this.loading = false
      }
    },
    showModal () {
      this.isShow = true
      this.initRenewModal()
    },
    initRenewModal () {
      this.selectedPeriod = this.bestDealPlanName
      this.fetchRenewalPricing()
      this.errorMessage = ''
      this.renewCompleted = false
      this.nextDueDate = ''
      this.totalSaved = ''
      this.renewalLoadError = false
      this.renewalLoadErrorMessage = ''
    },
    closeModal () {
      this.isShow = false
    },
    closeModalAfterRenewal () {
      this.$emit('renewal-complete', {
        serviceId: this.currentService.id,
        serviceType: this.currentService.type,
      })
      this.closeModal()
    },
    handleAutoRenew () {
      if (this.currentService.autorenew) {
        return this.$router.push({
          name: 'cancel',
          params: { service_id: this.currentService.id },
          query: { type: cancellationTypes.HOSTING },
        })
      }
      this.$refs.autoRenewModal.canAutorenewProceed()
    },
    serviceCalculatePricePerMonth (price, period) {
      let months = 1
      switch (period) {
        case PRODUCTS_BILLING_PERIODS.BIENNIALLY:
          months = 24
          break
        case PRODUCTS_BILLING_PERIODS.TRIENNIALLY:
          months = 36
          break
        case PRODUCTS_BILLING_PERIODS.SEMIANNUALLY:
          months = 6
          break
        case PRODUCTS_BILLING_PERIODS.QUARTERLY:
          months = 3
          break
        case PRODUCTS_BILLING_PERIODS.ANNUALLY:
        default:
          months = 12
          break
      }
      return ((parseFloat(price) * 100 / months) / 100).toFixed(2)
    },
    async fetchRenewalPricing () {
      this.loadingRenewalPricing = true
      this.renewalLoadError = false
      try {
        await this.getRenewalPricing({ serviceId: this.currentService.id })
      } catch (e) {
        this.reportError(e)
        this.renewalLoadError = true
        this.renewalLoadErrorMessage = typeof e === 'string' ? e : e.message || ''
        return
      } finally {
        this.loadingRenewalPricing = false
      }

      this.$nextTick(() => {
        // set selected term period
        if (this.preferredTerm && this.renewalPricing[this.preferredTerm]) {
          this.selectedPeriod = this.preferredTerm
        } else if (this.preferredTerm && this.renewalPricing['annually']) {
          this.selectedPeriod = 'annually'
        } else if (this.bestDealPlanName) {
          this.selectedPeriod = this.bestDealPlanName
        } else {
          // Default to annually when no discounts available
          this.selectedPeriod = 'annually'
        }
      })
    },
    listenGateway (gateway) {
      this.paymentGateway = gateway
    },
    listenCard (card) {
      this.cardModel = card
    },
    listenCardNew (card) {
      this.isNewCard = card
    },
    submitPaypalForm (val, endpoint) {
      this.$refs.paypal_encrypted.value = val
      this.$refs.frm_paypal.action = endpoint
      this.$refs.frm_paypal.submit()
    },
    handleStartChat () {
      const lcApi = window.LC_API || {}
      if (lcApi.set_visitor_name) {
        lcApi.set_visitor_name(`${this.SessionProfile.firstname} ${this.SessionProfile.lastname}`)
      }
      if (lcApi.set_visitor_email) {
        lcApi.set_visitor_email(this.SessionProfile.email)
      }
      if (lcApi.set_custom_variables) {
        lcApi.set_custom_variables([
          { name: 'Customer ID', value: this.SessionProfile.accountId },
          { name: 'Subject', value: 'Early Renewal Assistance' },
          { name: 'Domain', value: this.hosting.domain || '' },
          { name: 'Service ID', value: this.currentService?.id || '' },
        ])
      }
      openChat()
      this.closeModal()
    },
    handleOpenTicket () {
      this.closeModal()
      this.$router.push({
        name: 'support_ticketnew',
        query: {
          d: 'Billing',
          s: this.currentService?.id || '',
          sbj: 'Early Renewal Assistance',
        },
      })
    },
  },
}
</script>

<style scoped>
.active {
  border: 2px solid green !important;
}
</style>
